import axios from "axios";
import React, { useState, useEffect } from "react";

interface OrganizationInfo {
  id: string;
  name: string;
  intactId: string;
}

type AuthType = "sharepoint" | "dropbox" | "microsoft";
type DropboxAccountType = "personal" | "team";

/** Which organization the consent is for, from the link — or why there is none. */
const OrganizationNote: React.FC<{ organization: OrganizationInfo | null; linkError: string | null; optional?: boolean }> = ({
  organization,
  linkError,
  optional,
}) => (
  <p className="p-b-24" style={{ fontSize: "0.875rem" }}>
    {organization
      ? <>For <b>{organization.name}</b> ({organization.intactId}).</>
      : linkError
        ? linkError
        : optional
          ? "Opened without a consent link: the tenant is recorded but not connected to an organization."
          : "Open the consent link you were sent to connect an account."}
  </p>
);

const AdminConsentPage = () => {
  const [tenantName, setTenantName] = useState("");
  // The consent link's token is the page's only credential: it names the organization, and the server checks it.
  const [token, setToken] = useState<string | null>(null);
  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [organizationLoaded, setOrganizationLoaded] = useState(false);

  const [authType, setAuthType] = useState<AuthType>("sharepoint");
  const [dropboxAccountType, setDropboxAccountType] =
    useState<DropboxAccountType>("team");

  useEffect(() => {
    document.title = "Grant admin consent - Assembly Software";

    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement("link");
      (favicon as HTMLLinkElement).rel = "icon";
      document.head.appendChild(favicon);
    }
    if (favicon instanceof HTMLLinkElement) {
      favicon.href = "/adminconsent/images/icons/favicon.png";
    }

    const styleSheet = document.createElement("link");
    styleSheet.href = "/adminconsent/css/main.css";
    styleSheet.rel = "stylesheet";

    const font = document.createElement("link");
    font.href =
      "/adminconsent/fonts/font-awesome-4.7.0/css/font-awesome.min.css";
    font.rel = "stylesheet";

    document.head.appendChild(styleSheet);
    document.head.appendChild(font);

    const fetchData = async () => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const authTypeParam = urlParams.get("authType") as AuthType | null;
      const tokenParam = urlParams.get("token");

      if (
        authTypeParam &&
        (authTypeParam === "sharepoint" || authTypeParam === "dropbox" || authTypeParam === "microsoft")
      ) {
        setAuthType(authTypeParam);
      }

      if (tokenParam && !organizationLoaded) {
        setToken(tokenParam);
        const apiUrl = import.meta.env.VITE_API_URL ?? "/";
        const api = axios.create({
          baseURL: apiUrl,
          timeout: 30000,
        });

        try {
          const response = await api.get(`/api/adminconsent?token=${encodeURIComponent(tokenParam)}`);
          const found: OrganizationInfo = response.data;
          setOrganization(found);
          setTenantName(found.name);
        } catch (error) {
          setLinkError("This consent link is not valid or has expired. Ask your Assembly contact for a new one.");
          console.error("Error checking the consent link:", error);
        } finally {
          setOrganizationLoaded(true);
        }
      }
    };

    fetchData();

    return () => {
      document.head.removeChild(styleSheet);
      document.head.removeChild(font);
    };
  }, [organizationLoaded]);

  const handleSharePointSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tenantName.length === 0 || (token && !organization)) return;

    const azureAppClientId = "ca64f31f-2781-418c-9f74-7eae4c9b4665";
    const consentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${azureAppClientId}&state=${JSON.stringify(
      {
        tenantName,
        token: organization ? token : null,
      }
    )}&redirect_uri=https://${window.location.host}/adminconsent/result`;

    window.location.href = consentUrl;
  };

  const handleDropboxSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !organization) return;
    const rawApiUrl = import.meta.env.VITE_API_URL ?? "/";
    const apiUrl = rawApiUrl.endsWith("/") ? rawApiUrl : `${rawApiUrl}/`;
    const dropboxAuthUrl = `${apiUrl}api/adminconsent/dropbox?token=${encodeURIComponent(token)}&redirectUri=${encodeURIComponent(`https://${window.location.host}/adminconsent/result`)}&dropboxAccountType=${encodeURIComponent(dropboxAccountType)}`;
    window.location.href = dropboxAuthUrl;
  };

  const handleMicrosoftSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !organization) return;
    const rawApiUrl = import.meta.env.VITE_API_URL ?? "/";
    const apiUrl = rawApiUrl.endsWith("/") ? rawApiUrl : `${rawApiUrl}/`;
    const microsoftAuthUrl = `${apiUrl}api/adminconsent/microsoft?token=${encodeURIComponent(token)}&redirectUri=${encodeURIComponent(`https://${window.location.host}/adminconsent/result`)}`;
    window.location.href = microsoftAuthUrl;
  };

  return (
    <div className="limiter">
      <div
        className="container-login100"
        style={{
          backgroundImage:
            "url('/adminconsent/images/backgrounds/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        }}
      >
        <div className="wrap-login100">
          <div className="wrap-select100">
            <select
              className="input100"
              value={authType}
              onChange={(e) => setAuthType(e.target.value as AuthType)}
            >
              <option value="sharepoint">SharePoint</option>
              <option value="dropbox">Dropbox</option>
              <option value="microsoft">Microsoft (Delegated)</option>
            </select>
          </div>

          {/* SharePoint flow */}
          {authType === "sharepoint" && (
            <form
              className="login100-form validate-form"
              onSubmit={handleSharePointSubmit}
            >
              <span className="login100-form-title p-b-48">
                <img
                  width="250"
                  height="48"
                  src="/adminconsent/images/logos/assembly-logo.webp"
                  alt="Assembly Logo"
                />
              </span>
              <div
                className={
                  "wrap-input100" +
                  (tenantName.length > 0 ? "" : " alert-validate")
                }
                data-validate="Enter tenant name!"
              >
                <input
                  className="input100"
                  type="text"
                  placeholder="Tenant name"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                />
                <span className="focus-input100"></span>
              </div>
              <OrganizationNote organization={organization} linkError={linkError} optional />
              <div className="container-login100-form-btn">
                <div className="wrap-login100-form-btn">
                  <div className="login100-form-bgbtn"></div>
                  <button className="login100-form-btn" type="submit">
                    Grant admin consent (SharePoint)
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Dropbox flow */}
          {authType === "dropbox" && (
            <form
              className="login100-form validate-form"
              onSubmit={handleDropboxSubmit}
            >
              <span className="login100-form-title p-b-48">
                <img
                  width="250"
                  height="48"
                  src="/adminconsent/images/logos/assembly-logo.webp"
                  alt="Assembly Logo"
                />
              </span>
              <div className="wrap-select100">
                <label
                  htmlFor="dropbox-account-type"
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.75rem",
                  }}
                >
                  Account type
                </label>
                <select
                  className="input100"
                  id="dropbox-account-type"
                  value={dropboxAccountType}
                  onChange={(e) =>
                    setDropboxAccountType(e.target.value as DropboxAccountType)
                  }
                >
                  <option value="team">Team</option>
                  <option value="personal">Personal</option>
                </select>
              </div>
              <OrganizationNote organization={organization} linkError={linkError} />
              <div className="container-login100-form-btn">
                <div className="wrap-login100-form-btn">
                  <div className="login100-form-bgbtn"></div>
                  <button
                    className="login100-form-btn"
                    type="submit"
                    disabled={!organization}
                  >
                    Grant admin consent (Dropbox)
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Microsoft Delegated flow */}
          {authType === "microsoft" && (
            <form
              className="login100-form validate-form"
              onSubmit={handleMicrosoftSubmit}
            >
              <span className="login100-form-title p-b-48">
                <img
                  width="250"
                  height="48"
                  src="/adminconsent/images/logos/assembly-logo.webp"
                  alt="Assembly Logo"
                />
              </span>
              <OrganizationNote organization={organization} linkError={linkError} />
              <div className="container-login100-form-btn">
                <div className="wrap-login100-form-btn">
                  <div className="login100-form-bgbtn"></div>
                  <button
                    className="login100-form-btn"
                    type="submit"
                    disabled={!organization}
                  >
                    Grant delegated permissions (Microsoft)
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminConsentPage;
